'use client';
import { useEffect, useState, useCallback } from 'react';
import { VARIANTS_QUERY } from '@workspace/sanity-base/queries/variants';
import { client } from '@/sanity/lib/client';
import { linkResolver } from '@/sanity/lib/utils';
import type { VariantItem, ButtonDTO, ProductLinkTarget } from '@workspace/interfaces/product-variant.types';

const mapCmsLink = (cmsLink: any | undefined): ProductLinkTarget | undefined => {
  if (!cmsLink) return;

  const href =
    typeof cmsLink === 'string' ? cmsLink : typeof cmsLink?.href === 'string' ? cmsLink.href : linkResolver(cmsLink);

  if (!href || typeof href !== 'string') return;

  const newTab =
    (typeof cmsLink?.newTab === 'boolean' ? cmsLink.newTab : undefined) ??
    cmsLink?.openInNewTab ??
    cmsLink?.isOpenInNewTab ??
    false;

  return { href, newTab };
};

const mapVariant = (raw: any): VariantItem => ({
  heading: raw?.heading ?? raw?.name ?? '',
  subheading: raw?.subheading ?? raw?.description,
  productAttribute: raw?.productAttribute ?? raw?.variantValue,
  image: raw?.defaultImage || raw?.image || { asset: { url: '/images/placeholder.png' } },
  //link: mapCmsLink(raw?.link),
});

const mapButton = (raw?: any): ButtonDTO | undefined =>
  raw?.label && raw?.link
    ? {
        label: raw.label,
        link: mapCmsLink(raw.link),
        style: raw.style ?? 'PRIMARY',
      }
    : undefined;

const mapButtons = (raw?: any): ButtonDTO[] => {
  if (Array.isArray(raw)) {
    return raw.map(mapButton).filter((b): b is ButtonDTO => !!b);
  }
  if (raw) {
    const button = mapButton(raw);
    return button ? [button] : [];
  }
  return [];
};

export function useVariants({ skip = false }: { skip?: boolean } = {}) {
  const [heading, setHeading] = useState<string>('Product Variants');
  const [buttons, setButtons] = useState<ButtonDTO[]>([]);
  const [items, setItems] = useState<VariantItem[]>([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);

  const fetchVariants = useCallback(async () => {
    if (skip) return;
    try {
      setLoading(true);
      setError(null);

      const raw = await client.fetch(VARIANTS_QUERY);

      setHeading(raw?.heading ?? 'Product Variants');
      const cmsButtons = raw?.buttons || (raw?.button ? [raw.button] : []);
      setButtons(mapButtons(cmsButtons));
      setItems((raw?.productVariantItems ?? []).map(mapVariant));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch variants');
      console.error('Sanity variants error:', err);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  return { heading, buttons, items, loading, error, refetch: fetchVariants };
}
